import { useEffect } from "react";
import { useSSRHook } from "ssr-hook";
import { API, VPSStats } from "../../types";
import { useHead } from "../services/common";
import { Logo } from "../components/Logo";

function formatBytes(bytes: number) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function getProgressColor(percent: number) {
  if (percent > 90) return "danger";
  if (percent > 75) return "warning";
  return "";
}

export default function Index() {
  useHead("VPS SYSTEM MONITOR", "Technical dashboard for VPS monitoring.");

  const [stats, error, loading, reloadStats] =
    useSSRHook<API["getLatestStats"]["result"]>("/api/latest-stats");
  const [history, historyError, historyLoading, reloadHistory] =
    useSSRHook<API["getStatsHistory"]["result"]>("/api/stats-history");

  const handleRefresh = () => {
    reloadStats();
    reloadHistory();
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <Logo size={60} />
          <div>
            <h1>SYS_MONITOR</h1>
            <div style={{ color: "#00ff41", fontSize: "12px", opacity: 0.7 }}>
              ENDPOINT: VPS.PAULU.CZ
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <div className="timestamp">
            {stats
              ? `LAST_SYNC: ${new Date(stats.timestamp).toISOString()}`
              : "SYNCING..."}
          </div>
          <button
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={loading || historyLoading}
          >
            {(loading || historyLoading) ? "REFRESHING..." : "REFRESH"}
          </button>
        </div>
      </header>

      {error && (
        <div
          style={{
            color: "#ff003c",
            border: "1px solid #ff003c",
            padding: 10,
            marginBottom: 20,
          }}
        >
          CRITICAL_ERROR: {error.message}
        </div>
      )}

      {stats && (
        <>
          <div className="stats-grid">
            <StatCard
              title="CPU_LOAD"
              value={`${stats.cpu.currentLoad.toFixed(1)}%`}
              label={`CORES: ${stats.cpu.cores} | ${stats.cpu.speed}GHz`}
              percent={stats.cpu.currentLoad}
              history={history?.map((h) => h.cpu.currentLoad)}
              details={[
                { label: "AVG_LOAD", value: stats.cpu.avgLoad.toFixed(2) },
                {
                  label: "TEMP",
                  value: stats.cpu.temp ? `${stats.cpu.temp}°C` : "N/A",
                },
              ]}
            />
            <StatCard
              title="MEM_USAGE"
              value={`${(
                (stats.memory.active / stats.memory.total) *
                100
              ).toFixed(1)}%`}
              label={`${formatBytes(stats.memory.active)} / ${formatBytes(
                stats.memory.total,
              )}`}
              percent={(stats.memory.active / stats.memory.total) * 100}
              history={history?.map(
                (h) => (h.memory.active / h.memory.total) * 100,
              )}
              details={[
                {
                  label: "AVAILABLE",
                  value: formatBytes(stats.memory.available),
                },
                {
                  label: "SWAP",
                  value: `${(
                    (stats.memory.swapused / stats.memory.swaptotal) *
                    100
                  ).toFixed(1)}%`,
                },
              ]}
            />
            <StatCard
              title="DISK_SPACE"
              value={`${stats.disk.use}%`}
              label={`${formatBytes(stats.disk.used)} / ${formatBytes(
                stats.disk.total,
              )}`}
              percent={stats.disk.use}
              history={history?.map((h) => h.disk.use)}
              details={[
                {
                  label: "AVAILABLE",
                  value: formatBytes(stats.disk.available),
                },
                { label: "TOTAL", value: formatBytes(stats.disk.total) },
              ]}
            />
            <StatCard
              title="NETWORK_IO"
              value={stats.network[0]?.operstate.toUpperCase() || "N/A"}
              label={`IFACE: ${stats.network[0]?.iface || "NONE"}`}
              percent={0}
              hideProgress
              details={[
                {
                  label: "RX_TOTAL",
                  value: formatBytes(
                    stats.network.reduce((a, b) => a + b.rx_bytes, 0),
                  ),
                },
                {
                  label: "TX_TOTAL",
                  value: formatBytes(
                    stats.network.reduce((a, b) => a + b.tx_bytes, 0),
                  ),
                },
              ]}
            />
          </div>

          {stats.services?.postfix && (
            <div className="pm2-section">
              <h3>SYSTEM SERVICES</h3>
              <div className="pm2-grid">
                <div className={`pm2-card ${stats.services.postfix.status === "active" ? "online" : "error"}`}>
                  <div className="pm2-header">
                    <span className="pm2-name">Postfix (SMTP Inbound)</span>
                    <span className="pm2-status">
                      {stats.services.postfix.status === "active" ? "ONLINE" : stats.services.postfix.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="pm2-details">
                    <div><span>PORT 25:</span> {stats.services.postfix.port25 ? "LISTENING" : "CLOSED"}</div>
                    <div><span>QUEUE:</span> {stats.services.postfix.queueCount} msgs</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {stats.pm2 && stats.pm2.length > 0 && (
            <div className="pm2-section">
              <h3>PM2 PROCESSES</h3>
              <div className="pm2-grid">
                {stats.pm2.map((p, i) => (
                  <div key={i} className={`pm2-card ${p.status}`}>
                    <div className="pm2-header">
                      <span className="pm2-name">{p.name}</span>
                      <span className="pm2-status">{p.status.toUpperCase()}</span>
                    </div>
                    <div className="pm2-details">
                      <div><span>PID:</span> {p.pid}</div>
                      <div><span>MEM:</span> {formatBytes(p.memory)}</div>
                      <div><span>CPU:</span> {p.cpu}%</div>
                      <div><span>UPTIME:</span> {(p.uptime / 3600000).toFixed(1)}H</div>
                      <div><span>RESTARTS:</span> {p.restarts}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="os-info">
            <div>
              <span>OS:</span> {stats.os.distro} {stats.os.release} (
              {stats.os.arch})
            </div>
            <div>
              <span>KERNEL:</span> {stats.os.platform}
            </div>
            <div>
              <span>UPTIME:</span> {(stats.uptime / 3600).toFixed(2)} HOURS
            </div>
          </div>
        </>
      )}

      {loading && (
        <div style={{ color: "#00ff41", marginTop: 20 }}>
          FETCHING_SYSTEM_DATA...
        </div>
      )}
    </div>
  );
}

function StatCard({
  title,
  value,
  label,
  percent,
  details,
  history,
  hideProgress,
}: {
  title: string;
  value: string;
  label: string;
  percent: number;
  details: { label: string; value: string }[];
  history?: number[];
  hideProgress?: boolean;
}) {
  return (
    <div className="stat-card">
      <h3>{title}</h3>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>

      {!hideProgress && (
        <div className="progress-bar-bg">
          <div
            className={`progress-bar-fill ${getProgressColor(percent)}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}

      {history && history.length > 0 && (
        <div className="graph-container">
          {history.slice(-20).map((val, i) => (
            <div
              key={i}
              className="graph-bar"
              style={{ height: `${val}%` }}
              title={`${val.toFixed(1)}%`}
            />
          ))}
        </div>
      )}

      <div className="stat-details">
        {details.map((d, i) => (
          <div key={i}>
            <span>{d.label}:</span>
            <span>{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
